import React, { useState, useContext, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { AuthContext } from "../../context/AuthContext";
import toast from "react-hot-toast";
import PaymentSuccessModal from "../../components/PaymentSuccessModal";
import { patientAppUrl, safePatientRedirect } from "../../Utils/siteUrls";

function PaymentMethodSuccess() {
  const { userId: contextUserId, accessToken: contextAccessToken } = useContext(AuthContext);
  const [loading, setLoading] = useState(true);
  const [success, setSuccess] = useState(false);
  const [redirectUrl, setRedirectUrl] = useState("/"); // Store redirect URL from backend
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const device = searchParams.get('device'); // Get device from URL (added by backend in Stripe redirect)

  // Get userId and accessToken from localStorage or context
  const getUserData = () => {
    const storedUserId = localStorage.getItem('user_id') || localStorage.getItem('userId');
    const storedToken = localStorage.getItem('access_token') || localStorage.getItem('token');
    return {
      userId: contextUserId || storedUserId,
      accessToken: contextAccessToken || storedToken
    };
  };

  useEffect(() => {
    const { userId, accessToken } = getUserData();
    
    if (!sessionId) {
      toast.error("Invalid session");
      navigate("/");
      return;
    }

    // Process payment even without token - backend verifies via Stripe session
    // Token is optional, but we'll use it if available
    handleCheckoutSuccess(userId, accessToken, sessionId);
  }, [sessionId, contextUserId, contextAccessToken]);

  const handleCheckoutSuccess = async (userId, accessToken, sessionId) => {
    try {
      console.log("Processing checkout success for session:", sessionId);
      
      // Build headers - token is optional since backend verifies via Stripe
      const headers = {
        "Content-Type": "application/json",
      };
      
      // Add auth token if available
      if (accessToken) {
        headers.Authorization = `Bearer ${accessToken}`;
      }
      
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/handle_checkout_success/`,
        {
          method: "POST",
          headers: headers,
          body: JSON.stringify({ session_id: sessionId }),
        }
      );

      const data = await response.json();
      console.log("Checkout success response:", data);
      console.log("Device from response:", data.device);
      console.log("Redirect URL from response:", data.redirect_url);
      console.log("Account activated:", data.account_activated);
      console.log("Access token:", data.access_token);

      if (!response.ok) {
        throw new Error(data.error || "Failed to save payment method");
      }

      // If this is a child account that was just activated, store the access token
      if (data.account_activated && data.access_token) {
        console.log("Child account activated - storing access token");
        localStorage.setItem('token', data.access_token);
        localStorage.setItem('access_token', data.access_token);
        if (data.user_id) {
          localStorage.setItem('userId', data.user_id);
          localStorage.setItem('user_id', data.user_id);
        }
        toast.success("Account activated successfully! You can now log in.");
      }

      setSuccess(true);
      if (data.redirect_url) {
        setRedirectUrl(data.redirect_url);
      }
      
    } catch (error) {
      console.error("Error:", error);
      toast.error(error.message || "Failed to process payment method");
      
      setTimeout(() => {
        window.location.href = patientAppUrl();
      }, 2000);
    } finally {
      setLoading(false);
    }
  };

  const handleModalClose = () => {
    window.location.href = safePatientRedirect(redirectUrl);
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      {loading ? (
        <div className="max-w-md w-full mx-auto p-8 text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto mb-4"></div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            Processing...
          </h2>
          <p className="text-gray-600">
            Please wait while we save your payment method
          </p>
        </div>
      ) : (
        <>
          {/* Show modal on success */}
          <PaymentSuccessModal isOpen={success} onClose={handleModalClose} />
          
          {/* Error state - show error message */}
          {!success && !loading && (
            <div className="max-w-md w-full mx-auto p-8 text-center">
              <svg
                className="w-16 h-16 text-red-500 mx-auto mb-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                Something went wrong
              </h2>
              <p className="text-gray-600 mb-4">
                We couldn't save your payment method.
              </p>
              <p className="text-sm text-gray-500">
                Redirecting...
              </p>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default PaymentMethodSuccess;

